import { CommonModule } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, Component, OnDestroy, ViewChild, inject } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { IonContent, IonicModule } from "@ionic/angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { UtilsMsoService } from "@mso-services/utilsMso.service";
import { EventsService } from "@providers/EventsService";
import { TrackingService } from "@providers/TrackingService";
import { UserService } from "@providers/UserService";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { SportsFreeBetsService } from "@sports-services/sports.free-bets.service";

import { MSO_PATHS } from "@shared-constants/routes";
import { CodereFooterComponent } from "@shared-mso-components/footer/codere-footer/codere-footer";
import { UserSectionTitle } from "@shared-mso-components/user-section-title/user-section-title";
import { EmptyState } from "@shared-payments-components/empty-state/empty-state";
import { CodereStyleMoneyComponent } from "@sports-components/codere-style-money/codere-style-money";
import { TipoFreeBet } from "@sports-pages/free-bets/components/tipo-free-bet/tipo-free-bet";
import { FreeBetAvailableDepositFb } from "@sports-pages/ticket/models/free-bet.model";
import { SportsFormatOddPipe } from "@sports-pipes/sports.format-odd.pipe";
import { SportsParseMoneyPipe } from "@sports-pipes/sports.parse-money.pipe";
import { Utils } from "@sports-utils/Utils";
import { Subject, of, switchMap, takeUntil } from "rxjs";

@Component({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: "free-bets-page",
  templateUrl: "free-bets.page.html",
  styleUrls: ["./free-bets.page.scss"],
  standalone: true,
  imports: [
    IonicModule,
    CommonModule,
    UserSectionTitle,
    TranslateModule,
    CodereStyleMoneyComponent,
    SportsFormatOddPipe,
    EmptyState,
    CodereFooterComponent,
    SportsParseMoneyPipe,
    TipoFreeBet,
    ReactiveFormsModule,
    FormsModule,
  ],
})
export class FreeBetsPage implements OnDestroy {
  @ViewChild(IonContent) content: IonContent;
  imageSource: string;
  fbType = "online";
  codereCardActive = true;

  activeFBOnline;
  activeFBLocal;

  aPendingFBOnline;
  aPendingFBLocal;

  indexSeeMorePendLocal: number;
  indexSeeMorePendOnline: number;

  phoneNumber: string;

  firstLoad = true;
  forbiddenFreebets = false;

  hasAPendingFBOnline = false;
  hasActiveFBOnline = false;
  hasAPendingFBLocal = false;
  hasActiveFBLocal = false;

  showFbOnline: boolean;
  showFbLocal: boolean;

  form: FormGroup;
  modelForm: { smsCode: string } = { smsCode: "" };
  errorSmsCode = "";

  globalVars: GlobalVarsModel;

  private readonly unsubscribe$ = new Subject();

  trackingService = inject(TrackingService);
  userService = inject(UserService);
  translate = inject(TranslateService);
  utils = inject(Utils);
  router = inject(Router);
  fb = inject(FormBuilder);
  deviceService = inject(DeviceService);
  newBaseService = inject(NewBaseService);
  eventService = inject(EventsService);
  utilsMsoService = inject(UtilsMsoService);
  sportsFreebetsService = inject(SportsFreeBetsService);

  isMobile = false;
  isDesktop = false;

  constructor() {
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    this.activeFBOnline = [];
    this.activeFBLocal = [];

    this.aPendingFBOnline = [];
    this.aPendingFBLocal = [];
    this.imageSource = "assets/global/img/freebet-ticket.png";

    this.indexSeeMorePendLocal = 0;
    this.indexSeeMorePendOnline = 0;

    this.newBaseService.getVars
      .pipe(
        switchMap((data: GlobalVarsModel) => {
          this.globalVars = data;
          this.codereCardActive = this.globalVars.user.codereCard.active;
          return of(null);
        }),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();

    this.form = this.fb.group({
      smsCode: [""],
    });
    this.updateView();
  }

  updateView() {
    this.hasAPendingFBOnline = !!this.aPendingFBOnline?.length;
    this.hasActiveFBOnline = !!this.activeFBOnline?.length;
    this.hasAPendingFBLocal = !!this.aPendingFBLocal?.length;
    this.hasActiveFBLocal = !!this.activeFBLocal?.length;

    this.showFbOnline = this.fbType === "online";
    this.showFbLocal = this.fbType === "presencial";
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  ionViewWillEnter() {
    this.trackingService.trackEvent(["AccesToFreeBets", "", "", "Acceder FreeBets", "event"]);
    this.eventService.publish("footer:refresh", this.content);
  }

  ionViewDidEnter() {
    this.updateData();
  }

  ionViewDidLoad() {
    this.eventService.publish("footer:refresh", this.content);
  }

  updateData() {
    this.utils.loader();
    this.sportsFreebetsService
      .getFreeBets()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: async (data) => {
          if (!data.success) {
            if (data.pendingSms) {
              this.utilsMsoService
                .getUserData()
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                  next: (userData) => {
                    this.eventService.publish("loader:dismiss", null);
                    if (!userData.sessionOut) {
                      this.phoneNumber = userData.NumMovil;
                    } else {
                      //disconnect the user
                      this.eventService.publish("user:sessionTimeout");
                    }
                  },
                  error: () => {
                    this.eventService.publish("loader:dismiss", null);
                    this.utils.showError(this.translate.instant("m_menu_serverFail"));
                  },
                });
            } else {
              this.eventService.publish("loader:dismiss", null);
              this.showAlertForForbiddenAction();
            }
            this.forbiddenFreebets = true;
          } else {
            this.eventService.publish("loader:dismiss", null);
            this.forbiddenFreebets = false;
            this.firstLoad = false;
            const preFb: FreeBetAvailableDepositFb[] = data.availableDepositFbs;
            const activeFb = data.data;

            if (activeFb !== null) {
              this.activeFBOnline = activeFb.filter((value) => !value.IsTerminalVoucher);
              this.activeFBLocal = activeFb.filter((value) => value.IsTerminalVoucher);
            }
            if (preFb !== null) {
              this.aPendingFBOnline = preFb
                .filter((value) => {
                  if (value.PromoType.indexOf("online") !== -1) {
                    return true;
                  } else {
                    return false;
                  }
                })
                .map(this.sportsFreebetsService.formatFb);

              this.aPendingFBLocal = preFb
                .filter((value) => {
                  if (value.PromoType.indexOf("online") === -1) {
                    return true;
                  } else {
                    return false;
                  }
                })
                .map(this.sportsFreebetsService.formatFb);
            }
          }
          this.updateView();
          this.eventService.publish("footer:refresh", this.content);
          await this.utils.closeLoader();
        },
        error: async () => {
          this.updateView();
          this.utils.showError(this.translate.instant("m_menu_serverFail"));
          await this.utils.closeLoader();
        },
      });
  }

  showMoreFreeBet(type: string) {
    switch (type) {
      case "presencialesFb":
        this.indexSeeMorePendLocal = this.indexSeeMorePendLocal + 1;
        break;
      case "onlineFb":
        this.indexSeeMorePendOnline = this.indexSeeMorePendOnline + 1;
        break;
    }
  }

  gotoNearLocal() {
    this.router.navigate(["NearestLocalPage", {}], {
      queryParams: { animate: false },
    });
  }

  checkCanSendCodereCard() {
    this.userService.checkCanSendCodere().subscribe(
      (data) => {
        if (!data.success) {
          this.userService.hasDeposit().subscribe(
            (depositData) => {
              if (depositData.success) {
                this.router.navigate(["CardRequestFormPage", {}], {
                  queryParams: { animate: false },
                });
              } else {
                this.eventService.publish("user:serverfail");
              }
            },
            () => {
              this.eventService.publish("user:serverfail:soft");
            },
          );
        } else {
          this.utils.confirm(
            false,
            this.translate.instant("tFail"),
            this.translate.instant("coderecardverifdocum"),
            this.translate.instant("tCancel"),
            () => {
              //
            },
            this.translate.instant("check"),
            () => {
              setTimeout(() => {
                this.router.navigate(["DocumentVerificationPage", {}]);
              }, 800);
            },
          );
        }
      },
      () => {
        this.utils.showError(this.translate.instant("GetCardOnlylink_send"), null);
      },
    );
  }

  accessToCheckCodereCard() {
    this.userService.checkCanSendCodere().subscribe(
      (data) => {
        if (!data.success) {
          this.router.navigate([`/${MSO_PATHS.BASE}/${MSO_PATHS.CardLinkPage}`]);
        } else {
          this.utils.confirm(
            false,
            this.translate.instant("tFail"),
            this.translate.instant("coderecardverifdocum"),
            this.translate.instant("tCancel"),
            () => {
              //
            },
            this.translate.instant("check"),
            () => {
              setTimeout(() => {
                this.router.navigate(["DocumentVerificationPage", {}]);
              }, 800);
            },
          );
        }
      },
      () => {
        this.utils.showError(this.translate.instant("GetCardOnlylink"), null);
      },
    );
  }

  checkFBCode() {
    const codFB = this.modelForm.smsCode;
    if (codFB === "") {
      this.utils.showError("Campo incorrecto");
    } else {
      this.userService.checkPhoneFreeBets(codFB).subscribe(
        (data) => {
          if (data.success) {
            this.errorSmsCode = "";
            this.utils.alert(false, "", this.translate.instant("correctcode"), "OK");
            this.updateData();
          } else {
            this.utils.showError(this.translate.instant("incorrectcode"));
            this.updateData();
          }
        },
        () => {
          this.utils.showError(this.translate.instant("m_menu_serverFail"));
        },
      );
    }
  }

  reSendFBCode() {
    this.userService.resendFBCode().subscribe(
      (data) => {
        if (data.success) {
          this.utils.alert(false, "", this.translate.instant("regsmsrecib"), "OK");
        } else {
          this.utils.alert(false, "", this.translate.instant("regsmsmore"), "OK");
        }
      },
      () => {
        this.utils.showError(this.translate.instant("m_menu_serverFail"));
      },
    );
  }

  goToVer() {
    this.router.navigate(["DocumentVerificationPage", {}]);
  }

  showAlertForForbiddenAction() {
    this.utils.alert(
      true,
      this.translate.instant("Attention"),
      this.translate.instant("SPO_FB_ForbiddenAction"),
      this.translate.instant("tAccept"),
      () => {
        this.router.navigate(["/"]);
      },
    );
  }
}
